import { FC } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import { usePlausible } from 'next-plausible';
import type { Project } from '@lib/types/project';

import 'react-toastify/dist/ReactToastify.css';

interface ProjectSidebarProps {
  project: Project;
  className?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  subject: 'Quote',
  message: '',
};

const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, 'Too short!')
    .max(25, 'Too long!')
    .required('First name is required.'),
  lastName: Yup.string()
    .min(2, 'Too short!')
    .max(35, 'Too long!')
    .required('Last name is required.'),
  email: Yup.string().email().required('Email is required.'),
  message: Yup.string().min(2).max(500).required('Please provide a message.'),
});

const ProjectSidebar: FC<ProjectSidebarProps> = ({ project, className }) => {
  const plausible = usePlausible();
  return (
    <div className={className}>
      <div className="rounded-lg bg-white sm:mx-auto sm:w-full sm:max-w-md sm:overflow-hidden">
        <div className="px-8 py-8">
          <div>
            <h2 className="text-2xl font-medium text-gray-700">
              Request a Quote
            </h2>
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (
              values: FormValues,
              { setSubmitting, resetForm }: FormikHelpers<FormValues>
            ) => {
              setSubmitting(true);
              await toast.promise(
                fetch('/api/contact', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(values),
                }).then(() => resetForm()),
                {
                  pending: {
                    render() {
                      return 'Submitting your request';
                    },
                    icon: false,
                    toastId: 'contact-form-pending',
                  },
                  success: {
                    render() {
                      return 'Thank you for reaching out to us! We will be in contact shortly!';
                    },
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    draggable: true,
                    toastId: 'contact-form-success',
                  },
                  error: {
                    render() {
                      return 'There was an issue submitting your request. Please try again later.';
                    },
                    toastId: 'contact-form-error',
                  },
                }
              );
              plausible('submitContactForm', {
                props: {
                  page: 'Project',
                },
              });
              setSubmitting(false);
            }}
          >
            {({ touched, errors, isSubmitting, isValid, dirty }) => (
              <Form className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-stone-900"
                  >
                    First name
                  </label>
                  <div className="relative mt-1">
                    <Field
                      className={cn(
                        errors.firstName
                          ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                          : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                        'block w-full rounded-md py-3 pr-10 sm:text-sm'
                      )}
                      type="text"
                      name="firstName"
                      id="firstName"
                      autoComplete="given-name"
                      aria-invalid={errors.firstName}
                    />
                    {errors.firstName && touched.firstName ? (
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon
                          className="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      </div>
                    ) : null}
                  </div>
                  <ErrorMessage name="firstName">
                    {(msg) => (
                      <p
                        className="mt-2 text-sm text-red-600"
                        id="firstName-error"
                      >
                        {msg}
                      </p>
                    )}
                  </ErrorMessage>
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-stone-900"
                  >
                    Last name
                  </label>
                  <div className="relative mt-1">
                    <Field
                      className={cn(
                        errors.lastName
                          ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                          : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                        'block w-full rounded-md py-3 pr-10 sm:text-sm'
                      )}
                      type="text"
                      name="lastName"
                      id="lastName"
                      autoComplete="family-name"
                      aria-invalid={errors.lastName}
                    />
                    {errors.lastName && touched.lastName ? (
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon
                          className="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      </div>
                    ) : null}
                  </div>
                  <ErrorMessage name="lastName">
                    {(msg) => (
                      <p
                        className="mt-2 text-sm text-red-600"
                        id="lastName-error"
                      >
                        {msg}
                      </p>
                    )}
                  </ErrorMessage>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-stone-900"
                  >
                    Email
                  </label>
                  <div className="relative mt-1">
                    <Field
                      className={cn(
                        errors.email
                          ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                          : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                        'block w-full rounded-md py-3 pr-10 sm:text-sm'
                      )}
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      aria-invalid={errors.email}
                    />
                    {errors.email && touched.email ? (
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon
                          className="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      </div>
                    ) : null}
                  </div>
                  <ErrorMessage name="email">
                    {(msg) => (
                      <p className="mt-2 text-sm text-red-600" id="email-error">
                        {msg}
                      </p>
                    )}
                  </ErrorMessage>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex justify-between">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-stone-900"
                    >
                      Message
                    </label>
                    <span id="message-max" className="text-sm text-stone-500">
                      Max. 500 characters
                    </span>
                  </div>
                  <div className="relative mt-1">
                    <Field
                      className={cn(
                        errors.message
                          ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                          : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                        'block w-full rounded-md py-3 pr-10 sm:text-sm'
                      )}
                      as="textarea"
                      id="message"
                      name="message"
                      rows={4}
                      aria-invalid={errors.message}
                      aria-describedby="message-max"
                    />
                    {errors.message && touched.message ? (
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon
                          className="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      </div>
                    ) : null}
                  </div>
                  <ErrorMessage name="message">
                    {(msg) => (
                      <p
                        className="mt-2 text-sm text-red-600"
                        id="message-error"
                      >
                        {msg}
                      </p>
                    )}
                  </ErrorMessage>
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={isSubmitting || !(isValid && dirty)}
                    className="flex w-full justify-center rounded-md border border-transparent bg-gradient-to-r from-rose-600 to-red-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:from-rose-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Send Request
                  </button>
                </div>
              </Form>
            )}
          </Formik>
          <ToastContainer />
        </div>
      </div>
    </div>
  );
};

export default ProjectSidebar;
